import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { SelectGroupHeader } from '../../shared/components/SelectGroupHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { ConfirmDialog } from '../../shared/components/Dialogs/ConfirmDialog';
import { DocumentTypeDetailsDialog } from '../../shared/components/Dialogs/DocumentTypeDetailsDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type {
  DocumentCategory,
  DocumentType,
  DocumentTypeEntityType,
  DocumentTypeImportance,
  DocumentTypeSection,
  DocumentTypeSectionImportance,
  DocumentTypeSectionVisualImportance,
  DocumentTypeStatus,
  EntityType,
} from '../../core/types/data-models';

interface EntityTypeRow {
  relation: DocumentTypeEntityType;
  entityType: EntityType;
}

// ─── helpers ─────────────────────────────────────────────────────────────────

/** Stable fallback for a document type saved before `relatedEntityTypes` existed — a fresh `[]` each render would re-fire the dialog's reset effect. */
const NO_RELATED_ENTITY_TYPES: string[] = [];

const IMPORTANCE_COLORS: Record<string, 'error' | 'warning' | 'default'> = {
  HIGH: 'error',
  MEDIUM: 'warning',
  LOW: 'default',
};

/** Build the breadcrumb path: Category › Root Type › … › current Type */
function buildPath(
  type: DocumentType,
  allTypes: DocumentType[],
  categories: DocumentCategory[],
): string[] {
  const parts: string[] = [];
  const cat = categories.find((c) => c.type === type.categoryType);
  if (cat) parts.push(cat.name);

  // Walk up the parent chain (guard against cycles, max 20)
  const chain: DocumentType[] = [];
  let cur: DocumentType | undefined = type;
  let safety = 0;
  while (cur) {
    chain.unshift(cur);
    if (cur.parentDocumentType == null) break;
    cur = allTypes.find((t) => t.type === cur!.parentDocumentType);
    if (++safety > 20) break;
  }
  chain.forEach((t) => parts.push(t.name));
  return parts;
}

/**
 * Flattens sections into a parent-first ordered list with a depth for each,
 * so sub-sections render nested under their parent. Roots have a null parent;
 * children match their parent's `type`. Each level is sorted by defaultOrder.
 */
function buildSectionHierarchy(
  sections: DocumentTypeSection[],
  parentType: string | null = null,
  depth = 0,
): { section: DocumentTypeSection; depth: number }[] {
  return sections
    .filter((s) => (s.parentDocumentTypeSectionType ?? null) === parentType)
    .sort((a, b) => (a.defaultOrder ?? Infinity) - (b.defaultOrder ?? Infinity))
    .flatMap((s) => [
      { section: s, depth },
      ...buildSectionHierarchy(sections, s.type, depth + 1),
    ]);
}

/**
 * Sections whose parent chain doesn't resolve all the way up to a root —
 * either their own `parentDocumentTypeSectionType` names a type nothing owns
 * anymore, or an ancestor's does. These are silently dropped by
 * `buildSectionHierarchy` (and, in the generated document, silently
 * re-parented to root instead) so they need a separate, visible list.
 */
function findUnlistedSections(sections: DocumentTypeSection[]): DocumentTypeSection[] {
  const visibleIds = new Set(buildSectionHierarchy(sections).map(({ section }) => section.id));
  return sections.filter((s) => !visibleIds.has(s.id));
}

/** Explains why an unlisted section is hidden, for the broken-reference banner. */
function describeUnlistedReason(section: DocumentTypeSection, sections: DocumentTypeSection[]): string {
  const parentExists = sections.some((s) => s.type === section.parentDocumentTypeSectionType);
  return parentExists
    ? "An ancestor section higher up the chain has a broken parent reference."
    : `Parent section type "${section.parentDocumentTypeSectionType}" no longer exists.`;
}

/** Sorts related section type-refs by their document type name, then by section title. */
function sortRelatedSections(
  relatedSectionTypes: string[],
  allSectionsLookup: DocumentTypeSection[],
  allTypes: DocumentType[],
): string[] {
  return relatedSectionTypes.slice().sort((a, b) => {
    const secA = allSectionsLookup.find((s) => s.type === a);
    const secB = allSectionsLookup.find((s) => s.type === b);
    const typeNameA = allTypes.find((t) => t.type === secA?.documentType)?.name ?? '';
    const typeNameB = allTypes.find((t) => t.type === secB?.documentType)?.name ?? '';
    return (
      typeNameA.localeCompare(typeNameB) ||
      (secA?.title ?? '').localeCompare(secB?.title ?? '')
    );
  });
}

/** Groups other document types' sections for the "Related Sections" pick list, sorted by document name then section title. */
function buildRelatedSectionOptions(
  allSectionsLookup: DocumentTypeSection[],
  allTypes: DocumentType[],
  excludeDocumentType: string,
): { docType: DocumentType; sections: DocumentTypeSection[] }[] {
  return allTypes
    .filter((t) => t.type !== excludeDocumentType)
    .map((t) => ({
      docType: t,
      sections: allSectionsLookup
        .filter((s) => s.documentType === t.type)
        .slice()
        .sort((a, b) => a.title.localeCompare(b.title)),
    }))
    .filter((g) => g.sections.length > 0)
    .sort((a, b) => a.docType.name.localeCompare(b.docType.name));
}

// ─── Export / Import ────────────────────────────────────────────────────────

interface DocumentTypeExportSection {
  title: string;
  type: string;
  description: string | null;
  documentType: string;
  parentDocumentTypeSectionType: string | null;
  defaultOrder: number | null;
  llmPrompt: string;
  llmPromptForVisual: string | null;
  relatedSections: string[];
  importance: DocumentTypeSectionImportance;
  visualPriority: DocumentTypeSectionVisualImportance | null;
  status: DocumentTypeStatus;
}

interface DocumentTypeExportFile {
  exportedAt: string;
  documentType: {
    name: string;
    label: string;
    type: string;
    description: string | null;
    llmPrompt: string | null;
    categoryType: string;
    parentDocumentType: string | null;
    importance: DocumentTypeImportance;
    order: number | null;
    status: DocumentTypeStatus;
    dependentDocumentTypes: string[];
    relatedSections: string[];
    useWebSearch: boolean;
    useProjectPrompt: boolean;
  };
  sections: DocumentTypeExportSection[];
}

/** Builds the exportable JSON snapshot of a document type and all of its sections. */
function buildExportFile(docType: DocumentType, sections: DocumentTypeSection[]): DocumentTypeExportFile {
  return {
    exportedAt: new Date().toISOString(),
    documentType: {
      name: docType.name,
      label: docType.label,
      type: docType.type,
      description: docType.description,
      llmPrompt: docType.llmPrompt,
      categoryType: docType.categoryType,
      parentDocumentType: docType.parentDocumentType,
      importance: docType.importance,
      order: docType.order,
      status: docType.status,
      dependentDocumentTypes: docType.dependentDocumentTypes,
      relatedSections: docType.relatedSections,
      useWebSearch: docType.useWebSearch,
      useProjectPrompt: docType.useProjectPrompt,
    },
    sections: sections.map((s) => ({
      title: s.title,
      type: s.type,
      description: s.description,
      documentType: s.documentType,
      parentDocumentTypeSectionType: s.parentDocumentTypeSectionType,
      defaultOrder: s.defaultOrder,
      llmPrompt: s.llmPrompt,
      llmPromptForVisual: s.llmPromptForVisual,
      relatedSections: s.relatedSections,
      importance: s.importance,
      visualPriority: s.visualPriority,
      status: s.status,
    })),
  };
}

/** Parses and validates an uploaded JSON file against the shape produced by `buildExportFile`. Throws a descriptive Error on any shape mismatch. */
function parseImportFile(json: unknown): DocumentTypeExportFile {
  if (typeof json !== 'object' || json === null) {
    throw new Error('Invalid file: expected a JSON object.');
  }
  const root = json as Record<string, unknown>;

  const dt = root.documentType;
  if (typeof dt !== 'object' || dt === null) {
    throw new Error('Invalid file: missing "documentType" object.');
  }
  const dtObj = dt as Record<string, unknown>;
  if (typeof dtObj.name !== 'string' || !dtObj.name.trim()) {
    throw new Error('Invalid file: "documentType.name" is required.');
  }
  if (typeof dtObj.label !== 'string' || !dtObj.label.trim()) {
    throw new Error('Invalid file: "documentType.label" is required.');
  }
  if (typeof dtObj.type !== 'string' || !dtObj.type.trim()) {
    throw new Error('Invalid file: "documentType.type" is required.');
  }
  if (typeof dtObj.categoryType !== 'string' || !dtObj.categoryType.trim()) {
    throw new Error('Invalid file: "documentType.categoryType" is required.');
  }

  const sectionsRaw = root.sections;
  if (!Array.isArray(sectionsRaw)) {
    throw new Error('Invalid file: "sections" must be an array.');
  }
  const sections: DocumentTypeExportSection[] = sectionsRaw.map((s, i) => {
    if (typeof s !== 'object' || s === null) {
      throw new Error(`Invalid file: sections[${i}] is not an object.`);
    }
    const sObj = s as Record<string, unknown>;
    if (typeof sObj.title !== 'string' || !sObj.title.trim()) {
      throw new Error(`Invalid file: sections[${i}].title is required.`);
    }
    if (typeof sObj.type !== 'string' || !sObj.type.trim()) {
      throw new Error(`Invalid file: sections[${i}].type is required.`);
    }
    if (typeof sObj.llmPrompt !== 'string') {
      throw new Error(`Invalid file: sections[${i}].llmPrompt is required.`);
    }
    return {
      title: sObj.title,
      type: sObj.type,
      description: typeof sObj.description === 'string' ? sObj.description : null,
      documentType: typeof sObj.documentType === 'string' ? sObj.documentType : '',
      parentDocumentTypeSectionType: typeof sObj.parentDocumentTypeSectionType === 'string' ? sObj.parentDocumentTypeSectionType : null,
      defaultOrder: typeof sObj.defaultOrder === 'number' ? sObj.defaultOrder : null,
      llmPrompt: sObj.llmPrompt,
      llmPromptForVisual: typeof sObj.llmPromptForVisual === 'string' ? sObj.llmPromptForVisual : null,
      relatedSections: Array.isArray(sObj.relatedSections) ? (sObj.relatedSections as string[]) : [],
      importance: (sObj.importance as DocumentTypeSectionImportance) ?? 'MEDIUM',
      visualPriority: (sObj.visualPriority as DocumentTypeSectionVisualImportance | null) ?? null,
      status: (sObj.status as DocumentTypeStatus) ?? 'Active',
    };
  });

  return {
    exportedAt: typeof root.exportedAt === 'string' ? root.exportedAt : new Date().toISOString(),
    documentType: {
      name: dtObj.name,
      label: dtObj.label,
      type: dtObj.type,
      description: typeof dtObj.description === 'string' ? dtObj.description : null,
      llmPrompt: typeof dtObj.llmPrompt === 'string' ? dtObj.llmPrompt : null,
      categoryType: dtObj.categoryType,
      parentDocumentType: typeof dtObj.parentDocumentType === 'string' ? dtObj.parentDocumentType : null,
      importance: (dtObj.importance as DocumentTypeImportance) ?? 'MEDIUM',
      order: typeof dtObj.order === 'number' ? dtObj.order : null,
      status: (dtObj.status as DocumentTypeStatus) ?? 'Active',
      dependentDocumentTypes: Array.isArray(dtObj.dependentDocumentTypes) ? (dtObj.dependentDocumentTypes as string[]) : [],
      relatedSections: Array.isArray(dtObj.relatedSections) ? (dtObj.relatedSections as string[]) : [],
      useWebSearch: Boolean(dtObj.useWebSearch),
      useProjectPrompt: Boolean(dtObj.useProjectPrompt),
    },
    sections,
  };
}

interface ImportPlan {
  data: DocumentTypeExportFile;
  toCreate: DocumentTypeExportSection[];
  toUpdate: DocumentTypeExportSection[];
  toDelete: DocumentTypeSection[];
  /** File sections whose `type` is already used by a section belonging to a *different* document type — creating these would fail on the backend's unique constraint. */
  conflicts: DocumentTypeExportSection[];
}

/** Diffs a parsed import file against the currently loaded state for this document type. */
function buildImportPlan(
  data: DocumentTypeExportFile,
  currentSections: DocumentTypeSection[],
  allSectionsLookup: DocumentTypeSection[],
  currentDocType: DocumentType,
): ImportPlan {
  const existingByType = new Map(currentSections.map((s) => [s.type, s]));
  const fileTypes = new Set(data.sections.map((s) => s.type));

  return {
    data,
    toCreate: data.sections.filter((s) => !existingByType.has(s.type)),
    toUpdate: data.sections.filter((s) => existingByType.has(s.type)),
    toDelete: currentSections.filter((s) => !fileTypes.has(s.type)),
    conflicts: data.sections.filter(
      (s) =>
        !existingByType.has(s.type) &&
        allSectionsLookup.some((existing) => existing.type === s.type && existing.documentType !== currentDocType.type),
    ),
  };
}

// ─── Import preview dialog ────────────────────────────────────────────────────

interface ImportPreviewDialogProps {
  open: boolean;
  plan: ImportPlan | null;
  currentType: string;
  loading: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

function ImportPreviewDialog({ open, plan, currentType, loading, error, onConfirm, onClose }: ImportPreviewDialogProps) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Document Type</DialogTitle>
      <DialogContent dividers>
        {plan && (
          <Stack spacing={2}>
            <Alert severity="warning">
              This will overwrite this document type's properties and replace its sections to exactly match the
              imported file. Sections not present in the file will be deleted. This cannot be undone.
            </Alert>

            {plan.data.documentType.type !== currentType && (
              <Alert severity="info">
                This file was exported from document type "{plan.data.documentType.type}". Its identifier will stay
                "{currentType}" — only the other properties and sections shown below will be applied.
              </Alert>
            )}

            {plan.conflicts.length > 0 && (
              <Alert severity="error">
                The following section type{plan.conflicts.length > 1 ? 's are' : ' is'} already used by another
                document type, so {plan.conflicts.length > 1 ? 'they' : 'it'} cannot be imported here:{' '}
                {plan.conflicts.map((c) => c.title).join(', ')}
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Typography variant="body2">
                Document type <strong>{plan.data.documentType.name}</strong> — all properties will be overwritten
                from the file.
              </Typography>
            </Box>

            {plan.toCreate.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Sections to create ({plan.toCreate.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                  {plan.toCreate.map((s) => <li key={s.type}><Typography variant="body2">{s.title}</Typography></li>)}
                </Box>
              </Box>
            )}

            {plan.toUpdate.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Sections to update ({plan.toUpdate.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                  {plan.toUpdate.map((s) => <li key={s.type}><Typography variant="body2">{s.title}</Typography></li>)}
                </Box>
              </Box>
            )}

            {plan.toDelete.length > 0 && (
              <Box>
                <Typography variant="caption" color="error" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Sections to delete ({plan.toDelete.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 3 }}>
                  {plan.toDelete.map((s) => <li key={s.id}><Typography variant="body2" color="error">{s.title}</Typography></li>)}
                </Box>
              </Box>
            )}

            {plan.toCreate.length === 0 && plan.toUpdate.length === 0 && plan.toDelete.length === 0 && (
              <Typography variant="body2" color="text.secondary">No section changes.</Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={loading || !plan || plan.conflicts.length > 0}
        >
          {loading ? 'Importing…' : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Related sections dialog (document type level) ─────────────────────────

interface RelatedSectionsDialogProps {
  open: boolean;
  docTypeId: number;
  currentType: string;
  initial: string[];
  allSectionsLookup: DocumentTypeSection[];
  allTypes: DocumentType[];
  onClose: () => void;
}

/** Lets the user add/remove which other documents' sections feed this document type as background context. */
function RelatedSectionsDialog({ open, docTypeId, currentType, initial, allSectionsLookup, allTypes, onClose }: RelatedSectionsDialogProps) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const groups = buildRelatedSectionOptions(allSectionsLookup, allTypes, currentType);

  useEffect(() => {
    if (open) {
      setSelected(initial);
      setError(null);
      setCollapsedGroups(new Set(groups.map((g) => g.docType.type)));
    }
  }, [open, initial]);

  const save = useMutation({
    mutationFn: () => resources.documentTypes.update(docTypeId, { relatedSections: selected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentType', docTypeId] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Related Sections</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <FormControl fullWidth>
          <InputLabel>Related sections</InputLabel>
          <Select
            multiple
            value={selected}
            onChange={(e) => setSelected(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
            input={<OutlinedInput label="Related sections" />}
            renderValue={(sel) =>
              (sel as string[]).map((v) => allSectionsLookup.find((s) => s.type === v)?.title ?? v).join(', ')
            }
            MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
          >
            {groups.flatMap((g) => {
              const isCollapsed = collapsedGroups.has(g.docType.type);
              return [
                <SelectGroupHeader
                  key={`group-${g.docType.type}`}
                  label={g.docType.name}
                  collapsed={isCollapsed}
                  onToggle={() => setCollapsedGroups((prev) => {
                    const next = new Set(prev);
                    if (next.has(g.docType.type)) next.delete(g.docType.type);
                    else next.add(g.docType.type);
                    return next;
                  })}
                />,
                ...(!isCollapsed ? g.sections.map((s) => (
                  <MenuItem key={s.type} value={s.type} sx={{ pl: 3 }}>
                    <Checkbox checked={selected.includes(s.type)} size="small" />
                    <ListItemText primary={s.title} />
                  </MenuItem>
                )) : []),
              ];
            })}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Related entity types dialog (document type level) ─────────────────────

interface RelatedEntityTypesDialogProps {
  open: boolean;
  docTypeId: number;
  initial: string[];
  catalog: EntityType[];
  onClose: () => void;
}

/** Lets the user pick which entity types' project entities feed this document type as background context. */
function RelatedEntityTypesDialog({ open, docTypeId, initial, catalog, onClose }: RelatedEntityTypesDialogProps) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(initial);
      setError(null);
    }
  }, [open, initial]);

  // Options are names, not catalog rows: `relatedEntityTypes` stores names, so a
  // stored name whose entity type was renamed or deleted still round-trips.
  const descriptions = new Map(catalog.map((t) => [t.name, t.description]));
  const options = Array.from(new Set([...catalog.map((t) => t.name), ...selected]))
    .sort((a, b) => a.localeCompare(b));

  const save = useMutation({
    mutationFn: () => resources.documentTypes.update(docTypeId, { relatedEntityTypes: selected }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentType', docTypeId] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Related Entity Types</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Autocomplete<string, true, false, false>
          multiple
          disableCloseOnSelect
          options={options}
          value={selected}
          onChange={(_e, next) => setSelected(next)}
          renderOption={(props, name, { selected: isSelected }) => {
            const { key, ...liProps } = props;
            return (
              <li key={key} {...liProps}>
                <Checkbox checked={isSelected} size="small" />
                <ListItemText primary={name} secondary={descriptions.get(name) ?? undefined} />
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Related entity types"
              placeholder={selected.length === 0 ? 'Type to search…' : undefined}
              autoFocus
            />
          )}
          slotProps={{ listbox: { style: { maxHeight: 360 } } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>Cancel</Button>
        <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Section dialog ───────────────────────────────────────────────────────────

interface SectionDialogProps {
  open: boolean;
  initial?: DocumentTypeSection | null;
  documentType: string;
  allSections: DocumentTypeSection[];
  allSectionsLookup: DocumentTypeSection[];
  allTypes: DocumentType[];
  onClose: () => void;
}

/** Section identifiers are UPPER_SNAKE — same shape the create flow derives from the title. */
function normalizeSectionType(value: string): string {
  return value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');
}

function SectionDialog({ open, initial, documentType, allSections, allSectionsLookup, allTypes, onClose }: SectionDialogProps) {
  const qc = useQueryClient();
  const [title, setTitle] = useState('');
  const [sectionType, setSectionType] = useState('');
  const [description, setDescription] = useState('');
  const [llmPrompt, setLlmPrompt] = useState('');
  const [llmPromptMode, setLlmPromptMode] = useState<'markdown' | 'preview'>('markdown');
  const [llmPromptForVisual, setLlmPromptForVisual] = useState('');
  const [llmPromptForVisualMode, setLlmPromptForVisualMode] = useState<'markdown' | 'preview'>('markdown');
  const [parentId, setParentId] = useState<string>('');
  const [relatedSections, setRelatedSections] = useState<string[]>([]);
  const [defaultOrder, setDefaultOrder] = useState<string>('0');
  const [importance, setImportance] = useState<string>('MEDIUM');
  const [visualPriority, setVisualPriority] = useState<string>('');
  const [status, setStatus] = useState<string>('Active');
  const [error, setError] = useState<string | null>(null);
  const [collapsedRelatedGroups, setCollapsedRelatedGroups] = useState<Set<string>>(new Set());
  const [visualBlockOpen, setVisualBlockOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title ?? '');
      setSectionType(initial?.type ?? '');
      setDescription(initial?.description ?? '');
      setLlmPrompt(initial?.llmPrompt ?? '');
      setLlmPromptMode('markdown');
      setLlmPromptForVisual(initial?.llmPromptForVisual ?? '');
      setLlmPromptForVisualMode('markdown');
      setParentId(initial?.parentDocumentTypeSectionType ?? '');
      setRelatedSections(initial?.relatedSections ?? []);
      setDefaultOrder(initial?.defaultOrder != null ? String(initial.defaultOrder) : '0');
      setImportance(initial?.importance ?? 'MEDIUM');
      setVisualPriority(initial?.visualPriority ?? '');
      setStatus(initial?.status ?? 'Active');
      setError(null);
      setVisualBlockOpen(false);
    }
  }, [open]);

  // On create the identifier is derived from the title; when editing it's the
  // dedicated field. An empty one has to be caught here, because FormDialog
  // renders its form with `noValidate` — `required` below only draws the
  // asterisk. `type` is globally unique, so a rename is validated against
  // every section, not just this doc type's.
  const normalizedType = normalizeSectionType(initial ? sectionType : title);
  const identifierError =
    normalizedType === ''
      ? initial
        ? 'Identifier is required'
        : 'Title must contain at least one letter or digit'
      : initial && allSectionsLookup.some((s) => s.id !== initial.id && s.type === normalizedType)
        ? 'Another section already uses this identifier'
        : null;

  // A section is only reachable in the UI through its owning document type, so
  // a blank `documentType` would save a row nobody can ever see again.
  const ownerError = documentType.trim() === ''
    ? 'This document type has no identifier — set its Label before adding sections.'
    : null;

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        title: title.trim(),
        description: description.trim() || null,
        llmPrompt: llmPrompt.trim(),
        llmPromptForVisual: llmPromptForVisual.trim() || null,
        documentType,
        parentDocumentTypeSectionType: parentId === '' ? null : parentId,
        relatedSections,
        defaultOrder: defaultOrder !== '' ? Number(defaultOrder) : null,
        importance: importance as DocumentTypeSection['importance'],
        visualPriority: visualPriority === '' ? null : (visualPriority as DocumentTypeSection['visualPriority']),
        status: status as DocumentTypeSection['status'],
      };
      // `type` is the stable identifier child sections and relatedSections
      // arrays reference by string. It's editable, but only sent when it
      // actually changed — the backend repoints every reference to the old
      // value in a transaction, and an unchanged type shouldn't pay for that.
      if (initial) {
        return resources.documentTypeSections.update(initial.id, {
          ...payload,
          ...(normalizedType !== initial.type ? { type: normalizedType as any } : {}),
        });
      }
      return resources.documentTypeSections.create({
        ...payload,
        type: normalizedType as any,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentTypeSections'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  // Candidates for "parent": all sections of the same doc type, except the one being edited
  const parentCandidates = allSections.filter((s) => s.id !== initial?.id);

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit Section' : 'New Section'}
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        const blocker = ownerError ?? identifierError;
        if (blocker) { setError(blocker); return; }
        save.mutate();
      }}
      loading={save.isPending}
      maxWidth="md"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField
            label="Title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            helperText={
              !initial
                ? (normalizedType ? `Identifier: ${normalizedType}` : 'Identifier is derived from the title.')
                : undefined
            }
          />
          {initial && (
            <TextField
              label="Identifier"
              required
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value.toUpperCase().replace(/[^A-Z0-9]+/g, '_'))}
              error={!!identifierError}
              helperText={
                identifierError
                  ?? (normalizedType !== initial.type
                    ? `Renaming from ${initial.type} — child sections and related-section links are repointed automatically.`
                    : 'Stable identifier referenced by child sections and related-section links.')
              }
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          )}
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
          />
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                LLM Prompt
              </Typography>
              <ToggleButtonGroup
                value={llmPromptMode}
                exclusive
                size="small"
                onChange={(_e, val) => { if (val) setLlmPromptMode(val); }}
              >
                <ToggleButton value="markdown">Markdown</ToggleButton>
                <ToggleButton value="preview">Preview</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {llmPromptMode === 'markdown' ? (
              <TextField
                required
                fullWidth
                value={llmPrompt}
                onChange={(e) => setLlmPrompt(e.target.value)}
                multiline
                rows={5}
              />
            ) : (
              <Box
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  minHeight: 140,
                  '& p': { mt: 0, mb: 1 },
                  '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
                  '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto' },
                  '& code': { fontSize: 12, fontFamily: 'monospace' },
                }}
              >
                {llmPrompt
                  ? <ReactMarkdown>{llmPrompt}</ReactMarkdown>
                  : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nothing to preview.</Typography>
                }
              </Box>
            )}
          </Box>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => setVisualBlockOpen((v) => !v)}
              sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: 'action.hover' }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                LLM Prompt For Visual (optional)
              </Typography>
              <IconButton size="small" tabIndex={-1}>
                  {visualBlockOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                </IconButton>
            </Stack>
            <Collapse in={visualBlockOpen}>
              <Box sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Box />
                  <ToggleButtonGroup
                    value={llmPromptForVisualMode}
                    exclusive
                    size="small"
                    onChange={(_e, val) => { if (val) setLlmPromptForVisualMode(val); }}
                  >
                    <ToggleButton value="markdown">Markdown</ToggleButton>
                    <ToggleButton value="preview">Preview</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                {llmPromptForVisualMode === 'markdown' ? (
                  <TextField
                    fullWidth
                    value={llmPromptForVisual}
                    onChange={(e) => setLlmPromptForVisual(e.target.value)}
                    multiline
                    rows={3}
                    helperText="Markdown / Jinja2 template used by the LLM agent to generate the section's visual"
                  />
                ) : (
                  <Box
                    sx={{
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      minHeight: 80,
                      '& p': { mt: 0, mb: 1 },
                      '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
                      '& pre': { bgcolor: 'action.hover', p: 1, borderRadius: 1, overflowX: 'auto' },
                      '& code': { fontSize: 12, fontFamily: 'monospace' },
                    }}
                  >
                    {llmPromptForVisual
                      ? <ReactMarkdown>{llmPromptForVisual}</ReactMarkdown>
                      : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nothing to preview.</Typography>
                    }
                  </Box>
                )}
                <TextField
                  select
                  label="Visual Priority (optional)"
                  value={visualPriority}
                  onChange={(e) => setVisualPriority(e.target.value)}
                  sx={{ mt: 2, width: '30%', minWidth: 180 }}
                >
                  <MenuItem value="">— none —</MenuItem>
                  {(['HIGH', 'MEDIUM', 'LOW'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                </TextField>
              </Box>
            </Collapse>
          </Box>
          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Parent section (optional)"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              sx={{ flex: 1 }}
            >
              <MenuItem value="">— none —</MenuItem>
              {parentCandidates.map((s) => (
                <MenuItem key={s.id} value={s.type}>{s.title}</MenuItem>
              ))}
            </TextField>
            <FormControl sx={{ flex: 1 }}>
            <InputLabel>Related sections</InputLabel>
            <Select
              multiple
              value={relatedSections}
              onChange={(e) => setRelatedSections(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              input={<OutlinedInput label="Related sections" />}
              renderValue={(selected) =>
                (selected as string[])
                  .map((v) => allSectionsLookup.find((s) => s.type === v)?.title ?? v)
                  .join(', ')
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {allTypes.flatMap((t) => {
                const typeSections = allSectionsLookup
                  .filter((s) => s.documentType === t.type && s.id !== initial?.id)
                  .sort((a, b) => (a.defaultOrder ?? Infinity) - (b.defaultOrder ?? Infinity));
                if (typeSections.length === 0) return [];
                const isCollapsed = collapsedRelatedGroups.has(t.type);
                return [
                  <SelectGroupHeader
                    key={`sect-group-${t.type}`}
                    label={t.name}
                    collapsed={isCollapsed}
                    onToggle={() => setCollapsedRelatedGroups((prev) => {
                      const next = new Set(prev);
                      if (next.has(t.type)) next.delete(t.type);
                      else next.add(t.type);
                      return next;
                    })}
                  />,
                  ...(!isCollapsed ? typeSections.map((s) => (
                    <MenuItem key={s.type} value={s.type} sx={{ pl: 3 }}>
                      <Checkbox checked={relatedSections.includes(s.type)} size="small" />
                      <ListItemText primary={s.title} />
                    </MenuItem>
                  )) : []),
                ];
              })}
            </Select>
          </FormControl>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField select label="Importance" value={importance} onChange={(e) => setImportance(e.target.value)} sx={{ flex: 1 }}>
              {(['HIGH', 'MEDIUM', 'LOW'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
            <TextField
              label="Default order"
              type="number"
              value={defaultOrder}
              onChange={(e) => setDefaultOrder(e.target.value)}
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ flex: 1 }}>
              {(['Active', 'Disabled'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── SectionCard ──────────────────────────────────────────────────────────────

interface SectionCardProps {
  section: DocumentTypeSection;
  depth: number;
  forceOpen: boolean | null;
  allSectionsLookup: DocumentTypeSection[];
  allTypes: DocumentType[];
  onEdit: (s: DocumentTypeSection) => void;
  onDelete: (s: DocumentTypeSection) => void;
  /** When set, renders the card in a red "broken reference" state with this explanation. */
  errorReason?: string;
}

function SectionCard({ section, depth, forceOpen, allSectionsLookup, allTypes, onEdit, onDelete, errorReason }: SectionCardProps) {
  const [open, setOpen] = useState(false);
  const [relatedSectionsOpen, setRelatedSectionsOpen] = useState(false);

  useEffect(() => {
    if (forceOpen !== null) setOpen(forceOpen);
  }, [forceOpen]);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: errorReason ? 'error.main' : 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 1,
        ml: depth * 4,
      }}
    >
      {/* Header row */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          py: 1,
          bgcolor: open ? 'background.paper' : (errorReason ? (t) => alpha(t.palette.error.main, 0.08) : 'action.hover'),
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover .row-actions': { opacity: 1 },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        >
          {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>

        <Chip
          label={section.defaultOrder ?? '—'}
          size="small"
          variant="outlined"
          sx={{ minWidth: 36, fontWeight: 600 }}
        />

        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          {section.title}
        </Typography>

        {errorReason && (
          <Tooltip title={errorReason}>
            <Chip
              label="Broken reference"
              size="small"
              color="error"
              sx={{ height: 18, fontSize: 10, fontWeight: 600 }}
            />
          </Tooltip>
        )}

        <Chip
          label={section.type}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }}
        />

        <Chip
          label={section.importance ?? 'MEDIUM'}
          size="small"
          color={IMPORTANCE_COLORS[section.importance] ?? 'default'}
          sx={{ height: 18, fontSize: 10 }}
        />

        {section.visualPriority && (
          <Chip
            label={`Visual: ${section.visualPriority}`}
            size="small"
            variant="outlined"
            color={IMPORTANCE_COLORS[section.visualPriority] ?? 'default'}
            sx={{ height: 18, fontSize: 10 }}
          />
        )}

        <Chip
          label={section.status}
          size="small"
          variant="outlined"
          color={section.status === 'Disabled' ? 'default' : 'success'}
          sx={{ height: 18, fontSize: 10 }}
        />

        <Stack
          direction="row"
          className="row-actions"
          sx={{ opacity: 0, transition: 'opacity 0.15s' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Tooltip title="Edit section">
            <IconButton size="small" onClick={() => onEdit(section)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete section">
            <IconButton size="small" color="error" onClick={() => onDelete(section)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Expanded body */}
      <Collapse in={open}>
        <Box sx={{ px: 3, pb: 2, pt: 1 }}>
          {errorReason && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{errorReason} Edit this section to fix or clear its parent.</Alert>
          )}
          {section.description && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Description
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5, whiteSpace: 'pre-wrap' }}>
                {section.description}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
            </>
          )}

          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            LLM Prompt
          </Typography>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              mb: 1.5,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1,
              fontSize: 12,
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 300,
              overflowY: 'auto',
            }}
          >
            {section.llmPrompt || <Typography component="span" variant="caption" color="text.disabled">— empty —</Typography>}
          </Box>

          {section.llmPromptForVisual && (
            <>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                LLM Prompt For Visual
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 0.5,
                  mb: 1.5,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 300,
                  overflowY: 'auto',
                }}
              >
                {section.llmPromptForVisual}
              </Box>
            </>
          )}

          {section.relatedSections.length > 0 && (
            <>
              <Divider sx={{ mb: 1.5 }} />
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => setRelatedSectionsOpen((v) => !v)}
                  sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: 'action.hover' }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Related Sections ({section.relatedSections.length})
                  </Typography>
                  <IconButton size="small" tabIndex={-1}>
                    {relatedSectionsOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                  </IconButton>
                </Stack>
                <Collapse in={relatedSectionsOpen}>
                  <Box sx={{ p: 1.5 }}>
                    {sortRelatedSections(section.relatedSections, allSectionsLookup, allTypes).map((relSectionType) => {
                      const relSec = allSectionsLookup.find((s) => s.type === relSectionType);
                      if (!relSec) return null;
                      const relDocType = allTypes.find((t) => t.type === relSec.documentType);
                      return (
                        <Stack key={relSectionType} direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.25 }}>
                          <Typography variant="body2" color="text.secondary" noWrap>{relDocType?.name ?? '?'}</Typography>
                          <Typography variant="body2" color="text.disabled">›</Typography>
                          <Typography variant="body2">{relSec.title}</Typography>
                        </Stack>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Entity type dialog ─────────────────────────────────────────────────────

/** Free-form entity type name -> the catalog's normalized UPPER_SNAKE_CASE form. */
function normalizeEntityTypeName(raw: string): string {
  return raw.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_');
}

interface EntityTypeDialogProps {
  open: boolean;
  /** Editing an existing relation (its entity type's description, and its own active flag) vs. adding a new one. */
  editing?: EntityTypeRow | null;
  documentTypeId: string;
  /** The full global catalog, for the pick list and for detecting an existing match. */
  catalog: EntityType[];
  /** Entity types already related to this document type — excluded from the pick list. */
  linkedEntityTypeIds: Set<number>;
  onClose: () => void;
}

function EntityTypeDialog({ open, editing, documentTypeId, catalog, linkedEntityTypeIds, onClose }: EntityTypeDialogProps) {
  const qc = useQueryClient();
  const [inputValue, setInputValue] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setInputValue(editing?.entityType.name ?? '');
      setDescription(editing?.entityType.description ?? '');
      setIsActive(editing?.relation.isActive ?? true);
      setError(null);
    }
  }, [open, editing]);

  const normalizedName = normalizeEntityTypeName(inputValue);
  const matched = editing ? editing.entityType : catalog.find((t) => t.name === normalizedName) ?? null;

  // While picking a brand new relation (not editing), mirror the description
  // from whatever catalog entry the typed name currently resolves to. `isActive`
  // is left alone — it belongs to the relation being created, not the catalog.
  useEffect(() => {
    if (!editing) {
      setDescription(matched?.description ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, matched?.id]);

  const pickListOptions = catalog.filter((t) => !linkedEntityTypeIds.has(t.id));
  const isExistingMatch = !editing && !!matched;

  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        await resources.entityTypes.update(editing.entityType.id, {
          description: description.trim() || null,
        });
        return resources.documentTypeEntityTypes.update(editing.relation.id, { isActive });
      }
      const entityTypeId = matched
        ? matched.id
        : (await resources.entityTypes.create({
            name: normalizedName,
            description: description.trim() || null,
          })).id;
      return resources.documentTypeEntityTypes.create({ documentTypeId, entityTypeId, isActive });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentTypeEntityTypes'] });
      qc.invalidateQueries({ queryKey: ['entityTypes'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={editing ? 'Edit Entity Type' : 'New Entity Type'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="sm"
      submitLabel={editing ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Autocomplete
            freeSolo
            options={pickListOptions}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.name)}
            inputValue={inputValue}
            onInputChange={(_e, val) => setInputValue(val)}
            disabled={!!editing}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Entity type"
                required
                autoFocus={!editing}
                helperText={
                  editing
                    ? 'Linked entity type — remove and re-add to point this row at a different one.'
                    : isExistingMatch
                      ? 'Existing entity type — this will add a relation to this document type.'
                      : 'Type to search, or enter a new name to add it to the catalog.'
                }
              />
            )}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={2}
            disabled={isExistingMatch}
            helperText={isExistingMatch ? "Edit from the entity type's own row to change this." : undefined}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">Active for this document type</Typography>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          </Stack>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── Link existing entity type dialog ──────────────────────────────────────

interface LinkEntityTypeDialogProps {
  open: boolean;
  documentTypeId: string;
  /** The full global catalog — the pick list excludes types already linked. */
  catalog: EntityType[];
  linkedEntityTypeIds: Set<number>;
  onClose: () => void;
}

/** Relates an already-cataloged entity type to this document type, with no option to create a new one. */
function LinkEntityTypeDialog({ open, documentTypeId, catalog, linkedEntityTypeIds, onClose }: LinkEntityTypeDialogProps) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<EntityType | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setIsActive(true);
      setError(null);
    }
  }, [open]);

  const options = catalog.filter((t) => !linkedEntityTypeIds.has(t.id)).sort((a, b) => a.name.localeCompare(b.name));

  const save = useMutation({
    mutationFn: () => {
      if (!selected) throw new Error('Pick an entity type to link.');
      return resources.documentTypeEntityTypes.create({ documentTypeId, entityTypeId: selected.id, isActive });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentTypeEntityTypes'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title="Link Existing Entity Type"
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="sm"
      submitLabel="Link"
      submitDisabled={!selected}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <Autocomplete
            options={options}
            getOptionLabel={(o) => o.name}
            value={selected}
            onChange={(_e, val) => setSelected(val)}
            renderInput={(params) => (
              <TextField {...params} label="Entity type" required autoFocus helperText="Only entity types already in the catalog and not yet linked to this document type are shown." />
            )}
          />
          {selected?.description && (
            <Typography variant="body2" color="text.secondary">{selected.description}</Typography>
          )}
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">Active for this document type</Typography>
            <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          </Stack>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DocumentTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const docTypeId = Number(id);

  // Load the target document type
  const { data: docType, isLoading: loadingType, error: typeError } = useQuery({
    queryKey: ['documentType', docTypeId],
    queryFn: () => resources.documentTypes.get(docTypeId),
    enabled: !isNaN(docTypeId),
  });

  // Load all types + categories for breadcrumb
  const { data: typeEnv } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: () => resources.documentTypes.list({ page: 1, pageSize: 500, sort: 'order' }),
  });
  const { data: catEnv } = useQuery({
    queryKey: ['documentCategories'],
    queryFn: () => resources.documentCategories.list({ page: 1, pageSize: 200, sort: 'order' }),
  });

  // Load sections for this document type
  const { data: sectionsEnv, isLoading: loadingSections } = useQuery({
    queryKey: ['documentTypeSections', docTypeId],
    queryFn: () =>
      resources.documentTypeSections.list({
        page: 1,
        pageSize: 500,
        sort: 'defaultOrder',
        filter: { documentType: docType?.type },
      }),
    enabled: !isNaN(docTypeId) && !!docType?.type,
  });

  // Load all sections across all doc types — needed to resolve relatedSections
  const { data: allSectionsEnv } = useQuery({
    queryKey: ['documentTypeSections'],
    queryFn: () => resources.documentTypeSections.list({ page: 1, pageSize: 2000, sort: 'defaultOrder' }),
  });

  // Load the entity type relations configured for this document type
  const { data: docTypeEntityTypesEnv, isLoading: loadingEntityTypes } = useQuery({
    queryKey: ['documentTypeEntityTypes', docTypeId],
    queryFn: () =>
      resources.documentTypeEntityTypes.list({
        page: 1,
        pageSize: 500,
        filter: { documentTypeId: docType?.type },
      }),
    enabled: !isNaN(docTypeId) && !!docType?.type,
  });

  // Load the global entity type catalog — used both to resolve the relations
  // above and as the pick list in the "New Entity Type" dialog.
  const { data: entityTypeCatalogEnv } = useQuery({
    queryKey: ['entityTypes'],
    queryFn: () => resources.entityTypes.list({ page: 1, pageSize: 2000, sort: 'name' }),
  });

  const qc = useQueryClient();

  const allTypes: DocumentType[] = (typeEnv?.data ?? []);
  const categories: DocumentCategory[] = (catEnv?.data ?? []);
  const allSectionsLookup: DocumentTypeSection[] = allSectionsEnv?.data ?? [];
  const allSections: DocumentTypeSection[] = (sectionsEnv?.data ?? []).slice().sort(
    (a, b) => (a.defaultOrder ?? Infinity) - (b.defaultOrder ?? Infinity),
  );
  const unlistedSections: DocumentTypeSection[] = findUnlistedSections(allSections);
  const entityTypeCatalog: EntityType[] = entityTypeCatalogEnv?.data ?? [];
  const entityTypeCatalogById = new Map(entityTypeCatalog.map((t) => [t.id, t]));
  const entityTypeRows: EntityTypeRow[] = (docTypeEntityTypesEnv?.data ?? [])
    .map((relation) => ({ relation, entityType: entityTypeCatalogById.get(relation.entityTypeId) }))
    .filter((row): row is EntityTypeRow => !!row.entityType)
    .sort((a, b) => a.entityType.name.localeCompare(b.entityType.name));
  const linkedEntityTypeIds = new Set(entityTypeRows.map((row) => row.entityType.id));
  const relatedEntityTypes: string[] = docType?.relatedEntityTypes ?? NO_RELATED_ENTITY_TYPES;

  const breadcrumb = docType ? buildPath(docType, allTypes, categories) : [];

  // ── Section dialog state ─────────────────────────────────────────────────
  const [sectionDialog, setSectionDialog] = useState<{
    open: boolean;
    editing: DocumentTypeSection | null;
  }>({ open: false, editing: null });

  const openNewSection = () => setSectionDialog({ open: true, editing: null });
  const openEditSection = (s: DocumentTypeSection) => setSectionDialog({ open: true, editing: s });
  const closeSectionDialog = () => setSectionDialog({ open: false, editing: null });

  // ── Collapse/expand all ──────────────────────────────────────────────────
  const [forceOpen, setForceOpen] = useState<boolean | null>(null);
  const [lastForced, setLastForced] = useState<boolean>(false);

  const triggerForce = (value: boolean) => {
    setLastForced(value);
    setForceOpen(value);
    setTimeout(() => setForceOpen(null), 50);
  };

  // ── LLM prompt edit ─────────────────────────────────────────────────────
  const [llmPromptDialogOpen, setLlmPromptDialogOpen] = useState(false);
  const [llmPromptMode, setLlmPromptMode] = useState<'edit' | 'view'>('edit');
  const [llmPromptDraft, setLlmPromptDraft] = useState('');
  const [llmPromptError, setLlmPromptError] = useState<string | null>(null);

  const openLlmPromptEdit = () => {
    setLlmPromptDraft(docType?.llmPrompt ?? '');
    setLlmPromptMode('edit');
    setLlmPromptError(null);
    setLlmPromptDialogOpen(true);
  };

  const updateLlmPromptMutation = useMutation({
    mutationFn: (prompt: string) =>
      resources.documentTypes.update(docTypeId, { llmPrompt: prompt.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentType', docTypeId] });
      setLlmPromptDialogOpen(false);
    },
    onError: (err) => setLlmPromptError(extractErrorMessage(err)),
  });

  // ── Use web search toggle ───────────────────────────────────────────────
  const updateUseWebSearchMutation = useMutation({
    mutationFn: (useWebSearch: boolean) => resources.documentTypes.update(docTypeId, { useWebSearch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documentType', docTypeId] }),
  });

  // ── Use project prompt toggle ───────────────────────────────────────────
  const updateUseProjectPromptMutation = useMutation({
    mutationFn: (useProjectPrompt: boolean) => resources.documentTypes.update(docTypeId, { useProjectPrompt }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documentType', docTypeId] }),
  });

  // ── LLM prompt preview ──────────────────────────────────────────────────
  const [previewOpen, setPreviewOpen] = useState(false);

  // ── Related sections collapse / edit ─────────────────────────────────────
  const [relatedSectionsOpen, setRelatedSectionsOpen] = useState(false);
  const [relatedSectionsDialogOpen, setRelatedSectionsDialogOpen] = useState(false);

  // ── Related entity types collapse / edit ─────────────────────────────────
  const [relatedEntityTypesOpen, setRelatedEntityTypesOpen] = useState(false);
  const [relatedEntityTypesDialogOpen, setRelatedEntityTypesDialogOpen] = useState(false);

  // ── Delete confirm ───────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<DocumentTypeSection | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (s: DocumentTypeSection) => resources.documentTypeSections.remove(s.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentTypeSections'] });
      setDeleteTarget(null);
    },
  });

  // ── Entity type dialog + delete confirm ─────────────────────────────────
  const [entityTypeDialog, setEntityTypeDialog] = useState<{
    open: boolean;
    editing: EntityTypeRow | null;
  }>({ open: false, editing: null });
  const [deleteEntityTypeTarget, setDeleteEntityTypeTarget] = useState<EntityTypeRow | null>(null);

  const openNewEntityType = () => setEntityTypeDialog({ open: true, editing: null });
  const openEditEntityType = (row: EntityTypeRow) => setEntityTypeDialog({ open: true, editing: row });
  const closeEntityTypeDialog = () => setEntityTypeDialog({ open: false, editing: null });

  const [linkEntityTypeDialogOpen, setLinkEntityTypeDialogOpen] = useState(false);

  const toggleEntityTypeActiveMutation = useMutation({
    mutationFn: (relation: DocumentTypeEntityType) => resources.documentTypeEntityTypes.update(relation.id, { isActive: !relation.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documentTypeEntityTypes'] }),
  });

  const deleteEntityTypeMutation = useMutation({
    mutationFn: (row: EntityTypeRow) => resources.documentTypeEntityTypes.remove(row.relation.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentTypeEntityTypes'] });
      setDeleteEntityTypeTarget(null);
    },
  });

  // ── Export / Import ─────────────────────────────────────────────────────
  const importInputRef = useRef<HTMLInputElement>(null);
  const [importFileError, setImportFileError] = useState<string | null>(null);
  const [importPlan, setImportPlan] = useState<ImportPlan | null>(null);

  const handleExport = () => {
    if (!docType) return;
    const exportData = buildExportFile(docType, allSections);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `document-type-${docType.type}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !docType) return;
    setImportFileError(null);
    try {
      const text = await file.text();
      const data = parseImportFile(JSON.parse(text));
      setImportPlan(buildImportPlan(data, allSections, allSectionsLookup, docType));
    } catch (err) {
      setImportFileError(err instanceof Error ? err.message : 'Failed to parse the selected file.');
    }
  };

  const importMutation = useMutation({
    mutationFn: async (plan: ImportPlan) => {
      const dt = plan.data.documentType;
      await resources.documentTypes.update(docTypeId, {
        name: dt.name,
        label: dt.label,
        description: dt.description,
        llmPrompt: dt.llmPrompt,
        categoryType: dt.categoryType,
        parentDocumentType: dt.parentDocumentType,
        importance: dt.importance,
        order: dt.order,
        status: dt.status,
        dependentDocumentTypes: dt.dependentDocumentTypes,
        relatedSections: dt.relatedSections,
        useWebSearch: dt.useWebSearch,
        useProjectPrompt: dt.useProjectPrompt,
      });

      for (const s of plan.toDelete) {
        await resources.documentTypeSections.remove(s.id);
      }

      const existingByType = new Map(allSections.map((s) => [s.type, s]));
      for (const s of [...plan.toUpdate, ...plan.toCreate]) {
        const body = {
          title: s.title,
          description: s.description,
          documentType: docType!.type,
          parentDocumentTypeSectionType: s.parentDocumentTypeSectionType,
          defaultOrder: s.defaultOrder,
          llmPrompt: s.llmPrompt,
          llmPromptForVisual: s.llmPromptForVisual,
          relatedSections: s.relatedSections,
          importance: s.importance,
          visualPriority: s.visualPriority,
          status: s.status,
        };
        const existing = existingByType.get(s.type);
        if (existing) {
          await resources.documentTypeSections.update(existing.id, body);
        } else {
          await resources.documentTypeSections.create({ ...body, type: s.type });
        }
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['documentType', docTypeId] });
      qc.invalidateQueries({ queryKey: ['documentTypeSections'] });
      qc.invalidateQueries({ queryKey: ['documentTypes'] });
      qc.invalidateQueries({ queryKey: ['documentCategories'] });
      setImportPlan(null);
    },
  });

  if (loadingType) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (typeError || !docType) {
    return <Alert severity="error">{typeError ? extractErrorMessage(typeError) : 'Document type not found.'}</Alert>;
  }

  return (
    <Box>
      {/* Back button + header */}
      <PageHeader
        title={docType.name}
        description={docType.description ?? undefined}
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExport}>
              Export
            </Button>
            <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => importInputRef.current?.click()}>
              Import
            </Button>
            <input
              ref={importInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={handleImportFileChange}
            />
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/global-settings/document-management')}
            >
              Back
            </Button>
          </Stack>
        }
      />

      {importFileError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImportFileError(null)}>
          {importFileError}
        </Alert>
      )}

      {/* Breadcrumb path — directly under the document type name */}
      {breadcrumb.length > 0 && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 3 }}>
          {breadcrumb.map((part, i) => (
            <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
              {i > 0 && (
                <Typography variant="body2" color="text.disabled" sx={{ mx: 0.25 }}>›</Typography>
              )}
              <Typography
                variant="body2"
                color={i === breadcrumb.length - 1 ? 'text.primary' : 'text.secondary'}
                fontWeight={i === breadcrumb.length - 1 ? 600 : 400}
              >
                {part}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}

      {/* Use web search */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box>
          <Typography variant="body2" fontWeight={600}>Use Web Search</Typography>
          <Typography variant="caption" color="text.secondary">
            When enabled, the document generation job runs its LLM prompt with web search.
          </Typography>
        </Box>
        <Switch
          checked={docType.useWebSearch}
          onChange={(e) => updateUseWebSearchMutation.mutate(e.target.checked)}
          disabled={updateUseWebSearchMutation.isPending}
        />
      </Stack>

      {/* Use project prompt */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box>
          <Typography variant="body2" fontWeight={600}>Use Project Prompt</Typography>
          <Typography variant="caption" color="text.secondary">
            When enabled, the document generation job adds the project's own prompt to its LLM prompt as shared project context.
          </Typography>
        </Box>
        <Switch
          checked={docType.useProjectPrompt}
          onChange={(e) => updateUseProjectPromptMutation.mutate(e.target.checked)}
          disabled={updateUseProjectPromptMutation.isPending}
        />
      </Stack>

      {/* LLM Prompt */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 2, '& p': { mt: 0, mb: 1 }, '& ul, & ol': { mt: 0, mb: 1, pl: 3 }, '& pre': { bgcolor: 'background.paper', p: 1, borderRadius: 1, overflowX: 'auto' }, '& code': { fontSize: 12, fontFamily: 'monospace' } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            LLM Prompt
          </Typography>
          <Tooltip title="Edit LLM Prompt">
            <IconButton size="small" onClick={openLlmPromptEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {docType.llmPrompt
          ? <ReactMarkdown>{docType.llmPrompt}</ReactMarkdown>
          : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No LLM prompt set.</Typography>
        }
      </Box>

      <Box sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setRelatedSectionsOpen((v) => !v)}
          sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: 'action.hover' }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Related Sections ({docType.relatedSections.length})
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="Edit Related Sections">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRelatedSectionsDialogOpen(true); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" tabIndex={-1}>
              {relatedSectionsOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>
        <Collapse in={relatedSectionsOpen}>
          <Box sx={{ p: 1.5 }}>
            {docType.relatedSections.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No related sections set.</Typography>
            ) : (
              sortRelatedSections(docType.relatedSections, allSectionsLookup, allTypes).map((relSectionType) => {
                const relSec = allSectionsLookup.find((s) => s.type === relSectionType);
                if (!relSec) return null;
                const relDocType = allTypes.find((t) => t.type === relSec.documentType);
                return (
                  <Stack key={relSectionType} direction="row" alignItems="center" spacing={0.5} sx={{ py: 0.25 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>{relDocType?.name ?? '?'}</Typography>
                    <Typography variant="body2" color="text.disabled">›</Typography>
                    <Typography variant="body2">{relSec.title}</Typography>
                  </Stack>
                );
              })
            )}
          </Box>
        </Collapse>
      </Box>

      <RelatedSectionsDialog
        open={relatedSectionsDialogOpen}
        docTypeId={docTypeId}
        currentType={docType.type}
        initial={docType.relatedSections}
        allSectionsLookup={allSectionsLookup}
        allTypes={allTypes}
        onClose={() => setRelatedSectionsDialogOpen(false)}
      />

      <Box sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() => setRelatedEntityTypesOpen((v) => !v)}
          sx={{ px: 1.5, py: 1, cursor: 'pointer', bgcolor: 'action.hover' }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Related Entity Types ({relatedEntityTypes.length})
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Tooltip title="Edit Related Entity Types">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setRelatedEntityTypesDialogOpen(true); }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" tabIndex={-1}>
              {relatedEntityTypesOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>
        <Collapse in={relatedEntityTypesOpen}>
          <Box sx={{ p: 1.5 }}>
            {relatedEntityTypes.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>No related entity types set.</Typography>
            ) : (
              relatedEntityTypes.slice().sort((a, b) => a.localeCompare(b)).map((name) => {
                const entityType = entityTypeCatalog.find((t) => t.name === name);
                return (
                  <Stack key={name} direction="row" alignItems="baseline" spacing={1.5} sx={{ py: 0.25 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', flexShrink: 0 }}>{name}</Typography>
                    {entityType?.description && (
                      <Typography variant="caption" color="text.secondary" noWrap>{entityType.description}</Typography>
                    )}
                  </Stack>
                );
              })
            )}
          </Box>
        </Collapse>
      </Box>

      <RelatedEntityTypesDialog
        open={relatedEntityTypesDialogOpen}
        docTypeId={docTypeId}
        initial={relatedEntityTypes}
        catalog={entityTypeCatalog}
        onClose={() => setRelatedEntityTypesDialogOpen(false)}
      />

      <Divider sx={{ mb: 3 }} />

      {/* Sections header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Sections</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title={lastForced ? 'Collapse all' : 'Expand all'}>
            <IconButton
              size="small"
              onClick={() => triggerForce(!lastForced)}
              sx={{ border: '1px solid', borderColor: 'divider' }}
            >
              {lastForced ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button variant="outlined" onClick={() => setPreviewOpen(true)}>
            LLM Prompt Preview
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewSection}>
            New Section
          </Button>
        </Stack>
      </Stack>

      {/* Sections list */}
      {loadingSections ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : allSections.length === 0 ? (
        <Alert severity="info">No sections yet. Click "New Section" to add one.</Alert>
      ) : (
        buildSectionHierarchy(allSections).map(({ section: s, depth }) => (
          <SectionCard
            key={s.id}
            section={s}
            depth={depth}
            forceOpen={forceOpen}
            allSectionsLookup={allSectionsLookup}
            allTypes={allTypes}
            onEdit={openEditSection}
            onDelete={(sec) => setDeleteTarget(sec)}
          />
        ))
      )}

      {/* Sections with a broken parent reference — not shown above, listed here so they can be found and fixed */}
      {unlistedSections.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {unlistedSections.length} section{unlistedSections.length > 1 ? 's' : ''} {unlistedSections.length > 1 ? 'reference' : 'references'} a
            parent section that no longer exists, so {unlistedSections.length > 1 ? "they don't" : "it doesn't"} appear in the list above and
            won't be nested correctly in the generated document. Edit each one below to fix or clear its parent section.
          </Alert>
          {unlistedSections.map((s) => (
            <SectionCard
              key={s.id}
              section={s}
              depth={0}
              forceOpen={forceOpen}
              allSectionsLookup={allSectionsLookup}
              allTypes={allTypes}
              onEdit={openEditSection}
              onDelete={(sec) => setDeleteTarget(sec)}
              errorReason={describeUnlistedReason(s, allSections)}
            />
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Entity types header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>Entity Types</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setLinkEntityTypeDialogOpen(true)}>
            Link Existing Entity Type
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewEntityType}>
            New Entity Type
          </Button>
        </Stack>
      </Stack>

      {/* Entity types list */}
      {loadingEntityTypes ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
      ) : entityTypeRows.length === 0 ? (
        <Alert severity="info">No entity types yet. Click "New Entity Type" to add one.</Alert>
      ) : (
        entityTypeRows.map((row) => (
          <Box key={row.relation.id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 1, px: 2, py: 1.25, '&:hover .row-actions': { opacity: 1 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', flexShrink: 0 }}>{row.entityType.name}</Typography>
                {row.entityType.description && (
                  <Typography variant="caption" color="text.secondary" noWrap>{row.entityType.description}</Typography>
                )}
              </Stack>
              <Switch
                checked={row.relation.isActive}
                onChange={() => toggleEntityTypeActiveMutation.mutate(row.relation)}
                disabled={toggleEntityTypeActiveMutation.isPending}
              />
              <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                <Tooltip title="Edit entity type">
                  <IconButton size="small" onClick={() => openEditEntityType(row)}><EditIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Tooltip title="Remove from this document type">
                  <IconButton size="small" color="error" onClick={() => setDeleteEntityTypeTarget(row)}><DeleteIcon fontSize="small" /></IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        ))
      )}

      <EntityTypeDialog
        open={entityTypeDialog.open}
        editing={entityTypeDialog.editing}
        documentTypeId={docType.type}
        catalog={entityTypeCatalog}
        linkedEntityTypeIds={linkedEntityTypeIds}
        onClose={closeEntityTypeDialog}
      />

      <LinkEntityTypeDialog
        open={linkEntityTypeDialogOpen}
        documentTypeId={docType.type}
        catalog={entityTypeCatalog}
        linkedEntityTypeIds={linkedEntityTypeIds}
        onClose={() => setLinkEntityTypeDialogOpen(false)}
      />

      <ConfirmDialog
        open={deleteEntityTypeTarget !== null}
        title="Remove Entity Type"
        message={`Remove "${deleteEntityTypeTarget?.entityType.name}" from this document type? The entity type itself stays in the catalog.`}
        confirmLabel="Remove"
        destructive
        loading={deleteEntityTypeMutation.isPending}
        onConfirm={() => deleteEntityTypeTarget && deleteEntityTypeMutation.mutate(deleteEntityTypeTarget)}
        onClose={() => setDeleteEntityTypeTarget(null)}
      />

      {/* Add/Edit dialog */}
      <SectionDialog
        open={sectionDialog.open}
        initial={sectionDialog.editing}
        documentType={docType.type}
        allSections={allSections}
        allSectionsLookup={allSectionsLookup}
        allTypes={allTypes}
        onClose={closeSectionDialog}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Section"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      />

      {/* LLM Prompt Edit dialog */}
      <Dialog open={llmPromptDialogOpen} onClose={() => setLlmPromptDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <span>Edit LLM Prompt</span>
            <ToggleButtonGroup
              value={llmPromptMode}
              exclusive
              size="small"
              onChange={(_e, val) => { if (val) setLlmPromptMode(val); }}
            >
              <ToggleButton value="edit">Edit</ToggleButton>
              <ToggleButton value="view">View</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {llmPromptError && <Alert severity="error" sx={{ mb: 2 }}>{llmPromptError}</Alert>}
          {llmPromptMode === 'edit' ? (
            <TextField
              multiline
              fullWidth
              minRows={18}
              value={llmPromptDraft}
              onChange={(e) => setLlmPromptDraft(e.target.value)}
              placeholder="Enter Markdown / Jinja2 prompt…"
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />
          ) : (
            <Box
              sx={{
                '& h1': { fontSize: '1.5rem', fontWeight: 700, mt: 0, mb: 1 },
                '& h2': { fontSize: '1.15rem', fontWeight: 600, mt: 2.5, mb: 0.5 },
                '& p': { mt: 0, mb: 1 },
                '& ul, & ol': { mt: 0, mb: 1, pl: 3 },
                '& pre': { bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflowX: 'auto' },
                '& code': { fontSize: 12, fontFamily: 'monospace' },
              }}
            >
              {llmPromptDraft
                ? <ReactMarkdown>{llmPromptDraft}</ReactMarkdown>
                : <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>Nothing to preview.</Typography>
              }
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLlmPromptDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => updateLlmPromptMutation.mutate(llmPromptDraft)}
            disabled={updateLlmPromptMutation.isPending}
          >
            {updateLlmPromptMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* LLM Prompt Preview dialog */}
      <DocumentTypeDetailsDialog
        open={previewOpen}
        title={docType.name}
        documents={[{
          docType,
          sections: allSections,
          entityTypes: entityTypeRows.filter((row) => row.relation.isActive).map((row) => row.entityType),
        }]}
        onClose={() => setPreviewOpen(false)}
      />

      {/* Import preview / confirm dialog */}
      <ImportPreviewDialog
        open={importPlan !== null}
        plan={importPlan}
        currentType={docType.type}
        loading={importMutation.isPending}
        error={importMutation.error ? extractErrorMessage(importMutation.error) : null}
        onConfirm={() => importPlan && importMutation.mutate(importPlan)}
        onClose={() => { importMutation.reset(); setImportPlan(null); }}
      />
    </Box>
  );
}





